import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Grid, Stack, Divider, Chip, Tooltip, Button, Icon } from '@mui/material';
import { styled } from '@mui/styles';
import { isEmpty } from 'lodash-es';
import { useState, FC, useEffect, useCallback } from 'react';
import {
  IJob,
  getJobCandidateInteraction,
  getMyTeams,
  loadCandidateByCandidateId,
} from 'wisestep-api';
import {
  IConversationState,
  WsButton,
  WsColors,
  WsIconText,
  WsTypoColors,
  ICandidateAggregatedData,
  IUserDTO,
} from 'wisestep-base';

import { accessDeniedNotoficationTxt } from '../../../components/AccessControl/AccessControl';
import UpdateStatusModal from '../../../components/UpdateStatusModal/UpdateStatusModal';
import { useUserContext, useWorkspaceContext } from '../../../context';
import StageStatusContextProvider from '../../../features/submissions/context/StageStatusContext';
import { CandidateAtsAccess, useCandidateAts } from '../../../hooks/useCandidateAts';
import { warning, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { syncPostgreAndESCandidate } from '../../candidates/utils/CandidateHelper';
import { useJobScreenContext } from '../context/JobScreenContext';

import ConversationWidgetDrawer from './ConversationWidgetDrawer';
import MapConversationIcon from './MapConversationIcon';
import UpdateMapConversationIcon from './UpdateMapConversationIcon';

const Footer = styled(Grid)(() => ({
  textAlign: 'center',
}));

const Widget = styled(Grid)(() => ({
  borderRadius: '8px',
  background: WsColors.TheBlues900,
  padding: '20px 24px 20px 24px',
}));

const StyledAlternativeChip = styled(Chip)(() => ({
  borderRadius: 4,
  marginRight: 3,
  background: WsColors.TheBlues200,
  fontSize: '14px',
  color: WsColors.GenericRichBlack,
}));

const StyledChip = styled(Chip)(() => ({
  borderRadius: 4,
  marginRight: 3,
  background: WsColors.TheBlues800,
  fontSize: '14px',
  color: WsColors.TheBlues500,
}));

type ButtonType = {
  borderColor: string;
};

const IconButton = styled(Button)(({ borderColor }: ButtonType) => ({
  borderColor: borderColor,
}));

interface IConversationWidgetFooter {
  aggregation: ICandidateAggregatedData | null;
  state: IConversationState;
  selectedJob?: IJob | null;
}

const ConversationWidgetFooter: FC<IConversationWidgetFooter> = ({
  aggregation,
  state,
  selectedJob,
}) => {
  const [actionAfterJob, setActionAfterJob] = useState<string | null>(null);
  const [submissionPurpose, setSubmissionPurpose] = useState<string>('save');
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const closeUpdateStatusModal = () => setOpenStatusModal(false);
  const [status, setStatus] = useState<string | null>(null);
  const mappingAllowed = !isEmpty(aggregation?.jobInteraction);
  const updateAllowed = !isEmpty(aggregation?.recentInteractions);
  const jobInteractionId = aggregation?.jobInteraction?.id;
  const jobScreen = useJobScreenContext();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [updateMapping, setUpdateMapping] = useState<boolean>(false);
  const [submitCandidate, setSubmitCandidate] = useState<boolean>(false);
  const isJobCandidateMapping = aggregation?.jobInteraction?.id
    ? state.candidate?.isJobCandidateMapping
    : null;
  const view = useConversationWidgetStore((state) => state.view);
  const action = useConversationWidgetStore((state) => state.action);
  const updateAction = useConversationWidgetStore((state) => state.updateAction);
  const setIsAggLoaded = useConversationWidgetStore((state) => state.setIsAggLoaded);
  const setFooterAction = useConversationWidgetStore((state) => state.setFooterAction);
  const isAggLoaded = useConversationWidgetStore((state) => state.isAggLoaded);
  const [isJobAdminTeamMember, setIsJobAdminTeamMember] = useState<boolean>(false);
  const updateActivityConversation = useConversationWidgetStore(
    (state) => state.updateActivityConversation
  );
  const setActivityConversationFor = useConversationWidgetStore(
    (state) => state.setActivityConversationFor
  );

  const updateId = useConversationWidgetStore((state) => state.updateId);
  const setTempConv = useConversationWidgetStore((state) => state.setTempConv);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const updateView = useConversationWidgetStore((state) => state.updateView);
  const onWidgetClose = useConversationWidgetStore((state) => state.onWidgetClose);
  const workspace = useWorkspaceContext();

  useEffect(() => {
    if (action != 'smslogs') {
      action && setOpenDrawer(true);
    }
  }, [action]);

  const openWidget = useCallback(
    (status: string) => {
      setStatus(status);
      setOpenDrawer(true);
      updateAction('mapping');
    },
    [updateAction]
  );

  useEffect(() => {
    if (actionAfterJob && selectedJob && isAggLoaded) {
      if (
        actionAfterJob === 'CV Suitable' ||
        actionAfterJob === 'CV Not Suitable' ||
        actionAfterJob === 'Not Sure'
      ) {
        if (!isEmpty(aggregation?.jobInteraction)) {
          setNotification(
            warning(
              `Candidate is already ${isJobCandidateMapping ? 'mapped' : 'submitted'} to this job`
            )
          );
        } else {
          setFooterAction(actionAfterJob);
          openWidget(actionAfterJob);
          setIsAggLoaded(false);
        }
      } else if (
        actionAfterJob === 'email' ||
        actionAfterJob === 'sms' ||
        actionAfterJob === 'call' ||
        actionAfterJob === 'task' ||
        actionAfterJob === 'rtr'
      ) {
        updateAction(actionAfterJob);
        setIsAggLoaded(false);
      }
      setActionAfterJob(null);
    }
  }, [
    actionAfterJob,
    aggregation?.jobInteraction,
    isAggLoaded,
    isJobCandidateMapping,
    openWidget,
    selectedJob,
    setFooterAction,
    setIsAggLoaded,
    setNotification,
    updateAction,
  ]);

  const closeDrawer = () => {
    setUpdateMapping(false);
    setSubmitCandidate(false);
    setOpenDrawer(false);
    updateActivityConversation(null);
  };

  const openWidgetWithActions = (field: string) => {
    if (selectedJob) {
      setFooterAction(field);
      openWidget(field);
    } else {
      setIsAggLoaded(false);
      setActionAfterJob(field);
      setNotification(warning('Please select job first'));
      jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: true });
    }
  };

  const openModal = (field: string) => {
    if (selectedJob) {
      switch (field) {
        case 'Mapping':
          setActivityConversationFor('mapping');
          updateActivityConversation(state.candidate);
          openWidget('save');
          setUpdateMapping(true);
          break;
        case 'Submission':
          openWidget('');
          setSubmitCandidate(true);
          break;
      }
    } else {
      setNotification(warning('Please select job first'));
      jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: true });
    }
  };
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  useEffect(() => {
    (async () => {
      if (state.candidate.jobId) {
        const teamBelongsto = await getMyTeams();
        const currentUserTeamIds = teamBelongsto.data?.map((team) => team.id);
        setIsJobAdminTeamMember(currentUserTeamIds.includes(state.candidate?.jobPostedByTeam?.id));
      }
    })();
  }, [state]);

  const setActionFromBar = (action: 'email' | 'sms' | 'call' | 'task' | 'rtr' | 'mapping') => {
    if (selectedJob || view === 'submission') updateAction(action);
    else {
      setActionAfterJob(action);
      setNotification(warning('Please select job first'));
      jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: true });
    }
  };

  const updateSelectedJobConversation = async (interactionId: string | null) => {
    if (interactionId) {
      const res = await getJobCandidateInteraction(interactionId);
      if (res.data) {
        setActivityConversationFor('mapping');
        syncPostgreAndESCandidate(res.data, state.candidate);
        updateActivityConversation(res.data);
      } else {
        setNotification(failed('No Conversation found for update'));
      }
    } else {
      setNotification(warning('Please select job first'));
      jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: true });
    }
  };
  const isSubmitCandidate = useCandidateAts(CandidateAtsAccess.SUBMIT_CANDIDATE);
  const isRestrict = () => {
    // if (
    //   workspace.id === '0ed56aea-54c9-4485-987f-ffb35872eb38' ||
    //   workspace.id === '352f4535-62a5-4d74-8019-cb2ea76d8cb5'
    // ) {
    return true;
    // } else {
    //   return false;
    // }
  };
  return (
    <>
      <Footer>
        <Grid
          className="quick-actions"
          item
          mx={view === 'submission' ? '18%' : '30%'}
          display="flex"
          justifyContent="center"
          xs={!isRestrict() ? 12 : 6}
        >
          <Widget container>
            {view === 'submission' ? (
              <Grid item md={4} lg={5}>
                <Stack
                  direction="row"
                  justifyContent="space-evenly"
                  alignItems="center"
                  spacing={1}
                >
                  <StyledAlternativeChip
                    label={state.candidate?.applicationStage?.slice(0, 15) ?? 'Interview 3'}
                  />
                  <StyledChip label={state?.candidate?.applicationState?.slice(0, 17) ?? ''} />
                </Stack>
              </Grid>
            ) : (
              <Grid item md={!isRestrict() ? 6 : 2} lg={!isRestrict() ? 6 : 2}>
                {!isRestrict() && (
                  <>
                    {!mappingAllowed ? (
                      <Stack direction="row" spacing={3} justifyContent="center">
                        <Tooltip title="Map as CV Suitable">
                          <IconButton
                            variant="outlined"
                            borderColor={WsColors.Green500}
                            onClick={() => openWidgetWithActions('CV Suitable')}
                            startIcon={<CheckCircleRoundedIcon sx={{ color: WsColors.Green500 }} />}
                          >
                            Suitable
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Map as CV Not Suitable">
                          <IconButton
                            variant="outlined"
                            borderColor={WsColors.Red500}
                            onClick={() => openWidgetWithActions('CV Not Suitable')}
                            startIcon={<HighlightOffRoundedIcon sx={{ color: WsColors.Red500 }} />}
                          >
                            Not Suitable
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Map as Not Sure">
                          <IconButton
                            variant="outlined"
                            borderColor={WsColors.Yellow500}
                            onClick={() => openWidgetWithActions('Not Sure')}
                            startIcon={
                              <HelpRoundedIcon
                                sx={{
                                  color: WsColors.Yellow500,
                                }}
                              />
                            }
                          >
                            Not Sure
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <WsIconText
                        textCursor="pointer"
                        icon={<CheckCircleRoundedIcon sx={{ color: WsColors.Green500 }} />}
                        text={isJobCandidateMapping ? 'Mapped as Lead' : 'Submitted as Lead'}
                        textColor={WsTypoColors.WHITE}
                      />
                    )}
                  </>
                )}
              </Grid>
            )}

            <Grid
              item
              md={
                view === 'submission'
                  ? 8
                  : workspace.id !== '0ed56aea-54c9-4485-987f-ffb35872eb38'
                  ? 6
                  : 8
              }
              lg={
                view === 'submission'
                  ? 7
                  : workspace.id !== '0ed56aea-54c9-4485-987f-ffb35872eb38'
                  ? 6
                  : 8
              }
            >
              <Stack
                direction="row"
                spacing={3}
                justifyContent={view === 'submission' ? 'flex-end' : 'center'}
              >
                {view === 'mapping' && !isRestrict() && (
                  <Divider sx={{ bgcolor: WsColors.TheBlues800 }} orientation="vertical" flexItem />
                )}
                <Tooltip
                  title={
                    updateAllowed && jobInteractionId ? 'Update Conversation' : 'Map Conversation'
                  }
                >
                  <Icon
                    sx={{ color: WsColors.White, ':hover': { cursor: 'pointer' }, height: 30 }}
                    onClick={() =>
                      updateAllowed && jobInteractionId
                        ? updateSelectedJobConversation(jobInteractionId)
                        : setActionFromBar('mapping')
                    }
                  >
                    <>
                      {updateAllowed && jobInteractionId ? (
                        <UpdateMapConversationIcon />
                      ) : (
                        <MapConversationIcon />
                      )}
                    </>
                  </Icon>
                </Tooltip>
                <Tooltip title="Send Email">
                  <EmailOutlinedIcon
                    sx={{ color: WsColors.White, ':hover': { cursor: 'pointer' } }}
                    onClick={() => setActionFromBar('email')}
                  />
                </Tooltip>
                <Tooltip title="Coming Soon">
                  <LocalPhoneOutlinedIcon
                    sx={{ color: WsColors.TertiaryShades700, ':disabled': { disabled: 'true' } }}
                  />
                </Tooltip>
                <Tooltip title="Send SMS">
                  <MessageOutlinedIcon
                    sx={{ color: WsColors.White, ':hover': { cursor: 'pointer' } }}
                    onClick={() => setActionFromBar('sms')}
                  />
                </Tooltip>

                {(workspace.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e' ||
                  workspace.id === 'c809aa49-c696-4ea1-8467-0108a16ab786') && (
                  <Tooltip title="Candidate RTR">
                    <LocalLibraryOutlinedIcon
                      sx={{ color: WsColors.White, ':hover': { cursor: 'pointer' } }}
                      onClick={() => setActionFromBar('rtr')}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Setup a Reminder">
                  <TaskOutlinedIcon
                    sx={{ color: WsColors.White, ':hover': { cursor: 'pointer' } }}
                    onClick={() => setActionFromBar('task')}
                    className="add-a-reminder"
                  />
                </Tooltip>
                {view === 'submission' || isJobCandidateMapping === false ? (
                  <>
                    <WsButton
                      id="edit_submission"
                      variant="outlined"
                      onClick={async () => {
                        if (
                          isSubmitCandidate &&
                          aggregation &&
                          aggregation?.candidateEntity?.candidateId
                        ) {
                          const res = await loadCandidateByCandidateId(
                            aggregation.candidateEntity.candidateId
                          );
                          if (
                            loggedInUser?.email === state.candidate.modifiedBy ||
                            isJobAdminTeamMember
                          ) {
                            const submissionObject = state.candidate;
                            syncPostgreAndESCandidate(submissionObject, res.data);
                            updateActivityConversation(submissionObject);
                            openWidget('');
                            setSubmissionPurpose('update');
                            setSubmitCandidate(true);
                          } else {
                            setNotification(warning(accessDeniedNotoficationTxt));
                          }
                        } else {
                          setNotification(warning(accessDeniedNotoficationTxt));
                        }
                      }}
                    >
                      Edit Submission
                    </WsButton>

                    <WsButton
                      id="update_status"
                      variant="contained"
                      onClick={() => setOpenStatusModal(true)}
                    >
                      Update Status
                    </WsButton>
                  </>
                ) : (
                  <>
                    {/* {updateAllowed &&
                      jobInteractionId &&
                      workspace.id !== '0ed56aea-54c9-4485-987f-ffb35872eb38' && (
                        <WsButton
                          variant="outlined"
                          onClick={() => updateSelectedJobConversation(jobInteractionId)}
                        >
                          Update
                        </WsButton>
                      )} */}
                    <WsButton
                      variant="contained"
                      onClick={() => {
                        setSubmissionPurpose('save');
                        openModal('Submission');
                      }}
                    >
                      Submit
                    </WsButton>
                  </>
                )}
              </Stack>
            </Grid>
          </Widget>
        </Grid>
      </Footer>
      <ConversationWidgetDrawer
        open={openDrawer}
        aggregation={aggregation}
        updateMapping={updateMapping}
        submitCandidate={submitCandidate}
        state={state}
        recruiterAction={status}
        selectedJob={selectedJob}
        closeDrawer={closeDrawer}
        submissionPurpose={submissionPurpose}
      />
      {openStatusModal && (
        <StageStatusContextProvider>
          <UpdateStatusModal
            candidate={state.candidate}
            openStatusModal={openStatusModal}
            onClose={closeUpdateStatusModal}
            handleRefresh={(id: string | null) => {
              if (id) {
                setTempConv(null);
                onWidgetClose();
                updateFromPage('submission');
                updateView('submission');
                updateId(id);
              }
            }}
          />
        </StageStatusContextProvider>
      )}
    </>
  );
};

export default ConversationWidgetFooter;
